import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_ReferenceWebSafeFonts() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔤 CSS Reference: Web Safe Fonts</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Web safe fonts are pre-installed on most devices, ensuring text looks
        consistent across platforms without loading external font files.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#166534" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>Always include fallback fonts.</li>
          <li>
            Use <code>font-family</code> with multiple options → primary → safe
            → generic family.
          </li>
          <li>
            Common generic families: <code>serif</code>, <code>sans-serif</code>
            , <code>monospace</code>, <code>cursive</code>, <code>fantasy</code>
            .
          </li>
        </ul>
      </div>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Times New Roman (Serif)</h2>
        <p>
          This classic serif font is widely used in print and web typography.
        </p>
        <CodeCopyBlock
          code={`p {
  font-family: "Times New Roman", Times, serif;
}`}
        />
        <p
          style={{
            fontFamily: '"Times New Roman", Times, serif',
            fontSize: "32px",
          }}
        >
          Times New Roman Example Text
        </p>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Arial (Sans-serif)</h2>
        <p>A very common sans-serif font, clean and easy to read on screens.</p>
        <CodeCopyBlock
          code={`p {
  font-family: Arial, Helvetica, sans-serif;
}`}
        />
        <p
          style={{
            fontFamily: "Arial, Helvetica, sans-serif",
            fontSize: "32px",
          }}
        >
          Arial Example Text
        </p>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Verdana (Sans-serif)</h2>
        <p>
          Designed for screen readability, Verdana is wider and highly legible.
        </p>
        <CodeCopyBlock
          code={`p {
  font-family: Verdana, Geneva, sans-serif;
}`}
        />
        <p
          style={{
            fontFamily: "Verdana, Geneva, sans-serif",
            fontSize: "32px",
          }}
        >
          Verdana Example Text
        </p>
      </div>

      {/* Example 4 */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Tahoma (Sans-serif)</h2>
        <p>Similar to Verdana but more compact and modern in appearance.</p>
        <CodeCopyBlock
          code={`p {
  font-family: Tahoma, Geneva, sans-serif;
}`}
        />
        <p
          style={{ fontFamily: "Tahoma, Geneva, sans-serif", fontSize: "32px" }}
        >
          Tahoma Example Text
        </p>
      </div>

      {/* Example 5 */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Trebuchet MS (Sans-serif)</h2>
        <p>A humanist sans-serif font with a friendly and modern look.</p>
        <CodeCopyBlock
          code={`p {
  font-family: "Trebuchet MS", Helvetica, sans-serif;
}`}
        />
        <p
          style={{
            fontFamily: '"Trebuchet MS", Helvetica, sans-serif',
            fontSize: "32px",
          }}
        >
          Trebuchet MS Example Text
        </p>
      </div>

      {/* Example 6 */}
      <div className="tutorial-section">
        <h2 className="section-title">6️⃣ Georgia (Serif)</h2>
        <p>
          Georgia is a serif font designed for clarity on screens, elegant yet
          readable.
        </p>
        <CodeCopyBlock
          code={`p {
  font-family: Georgia, serif;
}`}
        />
        <p style={{ fontFamily: "Georgia, serif", fontSize: "32px" }}>
          Georgia Example Text
        </p>
      </div>

      {/* Example 7 */}
      <div className="tutorial-section">
        <h2 className="section-title">7️⃣ Garamond (Serif)</h2>
        <p>
          An old-style serif font, often used for books and elegant designs.
        </p>
        <CodeCopyBlock
          code={`p {
  font-family: Garamond, serif;
}`}
        />
        <p style={{ fontFamily: "Garamond, serif", fontSize: "32px" }}>
          Garamond Example Text
        </p>
      </div>

      {/* Example 8 */}
      <div className="tutorial-section">
        <h2 className="section-title">8️⃣ Courier New (Monospace)</h2>
        <p>A monospace font often used to display code or technical content.</p>
        <CodeCopyBlock
          code={`p {
  font-family: "Courier New", Courier, monospace;
}`}
        />
        <p
          style={{
            fontFamily: '"Courier New", Courier, monospace',
            fontSize: "32px",
          }}
        >
          Courier New Example Text
        </p>
      </div>

      {/* Example 9 */}
      <div className="tutorial-section">
        <h2 className="section-title">9️⃣ Brush Script MT (Cursive)</h2>
        <p>
          A cursive font that mimics handwritten text, used sparingly for style.
        </p>
        <CodeCopyBlock
          code={`p {
  font-family: "Brush Script MT", cursive;
}`}
        />
        <p
          style={{ fontFamily: '"Brush Script MT", cursive', fontSize: "32px" }}
        >
          Brush Script MT Example Text
        </p>
      </div>

      {/* Example 10 */}
      <div className="tutorial-section">
        <h2 className="section-title">🔟 Impact (Display Sans-serif)</h2>
        <p>A bold display font, commonly used for headlines and attention.</p>
        <CodeCopyBlock
          code={`p {
  font-family: Impact, Charcoal, sans-serif;
}`}
        />
        <p
          style={{
            fontFamily: "Impact, Charcoal, sans-serif",
            fontSize: "32px",
          }}
        >
          Impact Example Text
        </p>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde047)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#854d0e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>@font-face</code> for custom fonts.
          </li>
          <li>Try Google Fonts for modern typography.</li>
          <li>Test fonts on multiple devices for rendering consistency.</li>
          <li>Mix fonts responsibly to maintain readability.</li>
        </ul>
      </div>
    </div>
  );
}
